import { useState } from 'react';
import { scrollToTop } from '../../../utils/functions';

// ─── Default nav links (fallback) ─────────────────────────────────────────────
const DEFAULT_NAV_LINKS = [
    { label: 'Home', page: 'home' },
    { label: 'About Us', page: 'about' },
    { label: 'Milestones', page: 'milestones' },
    { label: 'Our Services', page: 'services' },
    { label: 'Online BO Open', page: 'account-open' },
    { label: 'News', page: 'news' },
    { label: 'Downloads', page: 'downloads' },
    { label: 'Privacy Policy', page: 'privacy-policy' },
    { label: 'Terms & Conditions', page: 'terms-page' },
];

const MenuIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// ─── Navbar Component ─────────────────────────────────────────────────────────
const Navbar = ({ attributes, currentPage, onNavigate, setClientTheme }) => {
    const { navbar = {}, colorTheme } = attributes;
    const isLight = colorTheme === 'light';
    const [menuOpen, setMenuOpen] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    // Merge with safe defaults so nothing breaks if a key is missing
    const cfg = {
        logoType: 'text',
        logoText: 'GMF',
        logoSubtext: 'Securities Ltd.',
        logoImg: '',
        loginButtonText: 'LOG IN',
        loginButtonLink: 'http://123.0.19.27/WebPortal/InvestorLogin.aspx',
        usePopup: false,
        popupTitle: 'Confirm Redirection',
        popupDesc: 'You are about to be redirected to an external trading platform. Do you wish to continue?',
        popupBtnCancel: 'Cancel',
        popupBtnGo: 'Continue',
        navLinks: DEFAULT_NAV_LINKS,
        stickyNavbar: true,
        navbarBlur: true,
        borderBottom: true,
        showIcons: true,
        showLoginBtn: true,
        activeColor: '#ff0037',
        loginBtnText: '#ffffff',
        ...navbar,
    };

    // Ensure navLinks is always an array
    const navLinks = Array.isArray(cfg.navLinks) && cfg.navLinks.length > 0
        ? cfg.navLinks
        : DEFAULT_NAV_LINKS;

    const handleNav = (page) => {
        onNavigate(page);
        scrollToTop();
        setMenuOpen(false);
    };

    const handleLoginClick = (e) => {
        if (e) e.preventDefault();
        if (cfg.usePopup) {
            setIsPopupOpen(true);
        } else {
            window.open(cfg.loginButtonLink, '_blank');
        }
    };

    const toggleTheme = () => {
        if (setClientTheme) {
            setClientTheme(isLight ? 'dark' : 'light');
        }
    };

    const SunIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
        </svg>
    );

    const MoonIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
        </svg>
    );

    const confirmRedirect = () => {
        setIsPopupOpen(false);
        window.open(cfg.loginButtonLink, '_blank');
    };

    // Dynamic inline styles driven by settings
    const navbarStyle = {
        position: cfg.stickyNavbar ? 'sticky' : 'relative',
        top: cfg.stickyNavbar ? 0 : 'auto',
        backdropFilter: cfg.navbarBlur ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: cfg.navbarBlur ? 'blur(12px)' : 'none',
        borderBottom: cfg.borderBottom
            ? (isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 255, 255, 0.05)')
            : 'none',
    };

    const activeLinkStyle = {
        color: cfg.activeColor,
        borderBottom: `2px solid ${cfg.activeColor}`,
        paddingBottom: 4,
        background: `${cfg.activeColor}10`,
    };

    const loginBtnStyle = {
        color: cfg.loginBtnText,
        // gradient or solid — keep the gradient from CSS but allow override
    };

    return (
        <nav className="gmf-navbar" style={navbarStyle}>
            <div className="gmf-navbar-inner">

                {/* ── Logo ── */}
                <button className="gmf-navbar-logo" onClick={() => handleNav('home')}>
                    {cfg.logoType === 'image' && cfg.logoImg ? (
                        <div className="gmf-navbar-logo-img-wrap">
                            <img src={cfg.logoImg} alt={cfg.logoSubtext} className="gmf-navbar-logo-img" />
                        </div>
                    ) : cfg.logoType === 'image-text' ? (
                        <>
                            {cfg.logoImg && (
                                <div className="gmf-navbar-logo-img-wrap" style={{ marginRight: '8px' }}>
                                    <img src={cfg.logoImg} alt={cfg.logoSubtext} className="gmf-navbar-logo-img" />
                                </div>
                            )}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', justifyContent: 'center' }}>
                                {cfg.logoText && <span style={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.1, color: 'inherit' }}>{cfg.logoText}</span>}
                                {cfg.logoSubtext && <span className="gmf-navbar-logo-text" style={{ marginLeft: 0, marginTop: '2px' }}>{cfg.logoSubtext}</span>}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="gmf-navbar-logo-circle gmf-accent-bg">
                                <span>{cfg.logoText}</span>
                            </div>
                            <span className="gmf-navbar-logo-text">{cfg.logoSubtext}</span>
                        </>
                    )}
                </button>

                {/* ── Desktop Links ── */}
                <div className="gmf-navbar-links">
                    {navLinks.map((link) => (
                        <button
                            key={link.page}
                            className={`gmf-nav-link ${currentPage === link.page ? 'gmf-nav-link--active' : ''}`}
                            style={currentPage === link.page ? activeLinkStyle : {}}
                            onClick={() => handleNav(link.page)}
                        >
                            {link.label}
                        </button>
                    ))}
                </div>

                {/* ── Right Actions ── */}
                <div className="gmf-navbar-actions">
                    {cfg.showIcons && (
                        <div className="gmf-navbar-icons">
                            <button className="gmf-icon-btn gmf-theme-toggle-btn" onClick={toggleTheme} aria-label="Toggle Theme" style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                                {isLight ? <MoonIcon /> : <SunIcon />}
                            </button>
                        </div>
                    )}

                    {cfg.showLoginBtn && (
                        <a
                            href={cfg.loginButtonLink || '#'}
                            className="gmf-btn-primary gmf-btn-sm"
                            style={loginBtnStyle}
                            onClick={handleLoginClick}
                        >
                            {cfg.loginButtonText}
                        </a>
                    )}

                    <button
                        className="gmf-mobile-menu-btn"
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        {menuOpen ? <CloseIcon /> : <MenuIcon />}
                    </button>
                </div>
            </div>

            {/* ── Mobile Menu ── */}
            <div className={`gmf-mobile-menu ${menuOpen ? 'gmf-mobile-menu--open' : ''}`}>
                {navLinks.map((link, index) => (
                    <button
                        key={link.page}
                        className={`gmf-mobile-nav-link ${currentPage === link.page ? 'gmf-nav-link--active' : ''}`}
                        style={{
                            ...(currentPage === link.page ? { color: cfg.activeColor } : {}),
                            animationDelay: `${index * 40}ms`,
                        }}
                        onClick={() => handleNav(link.page)}
                    >
                        {link.label}
                    </button>
                ))}

                {cfg.showLoginBtn && (
                    <a
                        href={cfg.loginButtonLink || '#'}
                        className="gmf-btn-primary gmf-btn-sm gmf-mobile-login"
                        style={loginBtnStyle}
                        onClick={handleLoginClick}
                    >
                        {cfg.loginButtonText}
                    </a>
                )}
            </div>

            {/* ── Confirmation Popup Modal ── */}
            {isPopupOpen && (
                <div className="gmf-modal-overlay">
                    <div className="gmf-modal-container">
                        <div className="gmf-modal-content">
                            <h3 className="gmf-modal-title">{cfg.popupTitle}</h3>
                            <p className="gmf-modal-desc">{cfg.popupDesc}</p>
                            <div className="gmf-modal-actions">
                                <button
                                    className="gmf-modal-btn gmf-modal-btn--cancel"
                                    onClick={() => setIsPopupOpen(false)}
                                >
                                    {cfg.popupBtnCancel}
                                </button>
                                <button
                                    className="gmf-modal-btn gmf-modal-btn--go gmf-accent-bg"
                                    onClick={confirmRedirect}
                                >
                                    {cfg.popupBtnGo}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
